import { apiClient } from '@/lib/api-client';

// Notification settings interface
export interface NotificationSetting {
    enabled: boolean;
    email: boolean;
    inApp: boolean;
    description: string;
}

export interface NotificationSettings {
    leadUpdates: NotificationSetting;
    estimatesUpdates: NotificationSetting;
    proposalUpdates: NotificationSetting;
    architectUpdates: NotificationSetting;
    ask: NotificationSetting;
    // #6 — sales-rep only: notify when a client is assigned to them. Optional
    // so existing saved settings without the key still parse.
    clientAssignmentNotification?: NotificationSetting;
}

// Payment milestone interface
export interface PaymentMilestone {
    id: string;
    name: string;
    percentage: number;
    fixedAmount: number;
    amountType: 'percentage' | 'fixed' | 'remainder';
    description: string;
    trigger: string;
    order: number;
}

// Settings interface
export interface Settings {
    notifications: NotificationSettings;
    paymentMilestones: PaymentMilestone[];
}

// API response interface (what we get from GET /settings/me)
export interface SettingsApiResponse {
    id: string;
    userId: string;
    settings: Settings;
    createdAt: string;
    updatedAt: string;
    user: {
        id: string;
        fullName: string;
        email: string;
    };
}

// API request interfaces
export interface CreateSettingsRequest {
    settings: {
        notifications: NotificationSettings;
        paymentMilestones: PaymentMilestone[];
    };
}

export interface UpdateSettingsRequest {
    settings: {
        notifications: NotificationSettings;
        paymentMilestones: PaymentMilestone[];
    };
}

export interface SettingsResponse {
    success: boolean;
    message: string;
    settings: SettingsApiResponse;
}

// Compliance Requirements interface
export interface ComplianceRequirementsConfig {
    requireSubcontractorAgreement: boolean;
    requireBond: boolean;
    /** Absent on current staging API; C-Back-End #101 may add this. */
    requireLicense?: boolean;
}

export const settingsService = {
    // Get user's settings
    getSettings: async (): Promise<Settings | null> => {
        try {
            const response = await apiClient.get<SettingsApiResponse>('/settings/me');
            // Extract the settings object from the API response
            return response.settings;
        } catch (error: any) {
            // If settings don't exist (404), return null
            if (error?.status === 404) {
                return null;
            }
            throw error;
        }
    },

    // Create new settings (first time)
    createSettings: async (data: CreateSettingsRequest): Promise<SettingsResponse> => {
        const response = await apiClient.post<SettingsResponse>('/settings', data);
        return response;
    },

    // Update existing settings
    updateSettings: async (data: UpdateSettingsRequest): Promise<SettingsResponse> => {
        const response = await apiClient.put<SettingsResponse>('/settings/me', data);
        return response;
    },

    // Get compliance requirements config
    getComplianceRequirements: async (): Promise<ComplianceRequirementsConfig> => {
        return await apiClient.get<ComplianceRequirementsConfig>('/settings/compliance-requirements');
    },

    // Update compliance requirements config
    updateComplianceRequirements: async (data: Partial<ComplianceRequirementsConfig>): Promise<ComplianceRequirementsConfig> => {
        return await apiClient.put<ComplianceRequirementsConfig>('/settings/compliance-requirements', data);
    },

    // Get proposal template language
    getProposalTemplate: async (): Promise<ProposalTemplateConfig> => {
        return await apiClient.get<ProposalTemplateConfig>('/settings/proposal-template');
    },

    // Update proposal template language
    updateProposalTemplate: async (data: Partial<ProposalTemplateConfig>): Promise<ProposalTemplateConfig> => {
        return await apiClient.put<ProposalTemplateConfig>('/settings/proposal-template', data);
    },

    getInvoiceEmailTemplate: async (): Promise<InvoiceEmailTemplateConfig> => {
        return await apiClient.get<InvoiceEmailTemplateConfig>('/settings/invoice-email-template');
    },

    updateInvoiceEmailTemplate: async (
        data: Pick<InvoiceEmailTemplateConfig, 'subject' | 'body'>,
    ): Promise<InvoiceEmailTemplateConfig> => {
        return await apiClient.put<InvoiceEmailTemplateConfig>('/settings/invoice-email-template', data);
    },

    /** Assigned product mailbox `team@{slug}.volentek.com`. Read-only display. */
    getTeamMail: async (): Promise<unknown> => {
        return await apiClient.get<unknown>('/settings/team-mail');
    },

    getGrokApiKeyStatus: async (): Promise<GrokApiKeyStatus> => {
        return await apiClient.get<GrokApiKeyStatus>('/settings/grok-api-key');
    },

    putGrokApiKey: async (apiKey: string): Promise<GrokApiKeyStatus> => {
        return await apiClient.put<GrokApiKeyStatus>('/settings/grok-api-key', { apiKey });
    },

    deleteGrokApiKey: async (): Promise<GrokApiKeyStatus> => {
        return await apiClient.delete<GrokApiKeyStatus>('/settings/grok-api-key');
    },

    listAdministrationCostDefaults: async (): Promise<AdministrationCostDefault[]> => {
        return await apiClient.get<AdministrationCostDefault[]>(
            '/settings/administration-cost-defaults',
        );
    },

    createAdministrationCostDefault: async (
        data: Pick<AdministrationCostDefault, 'purpose' | 'description' | 'quantity' | 'unitCost'>,
    ): Promise<AdministrationCostDefault> => {
        return await apiClient.post<AdministrationCostDefault>(
            '/settings/administration-cost-defaults',
            data,
        );
    },

    updateAdministrationCostDefault: async (
        id: string,
        data: Partial<Pick<AdministrationCostDefault, 'purpose' | 'description' | 'quantity' | 'unitCost'>>,
    ): Promise<AdministrationCostDefault> => {
        return await apiClient.patch<AdministrationCostDefault>(
            `/settings/administration-cost-defaults/${encodeURIComponent(id)}`,
            data,
        );
    },

    deleteAdministrationCostDefault: async (id: string): Promise<{ ok: true }> => {
        return await apiClient.delete<{ ok: true }>(
            `/settings/administration-cost-defaults/${encodeURIComponent(id)}`,
        );
    },

    reorderAdministrationCostDefaults: async (
        ids: string[],
    ): Promise<AdministrationCostDefault[]> => {
        return await apiClient.put<AdministrationCostDefault[]>(
            '/settings/administration-cost-defaults/reorder',
            { ids },
        );
    },

    getFinancingPrograms: async (): Promise<{ financingPrograms: FinancingProgramConfig[] }> => {
        return await apiClient.get<{ financingPrograms: FinancingProgramConfig[] }>(
            '/settings/financing-programs',
        );
    },

    updateFinancingPrograms: async (
        financingPrograms: FinancingProgramConfig[],
    ): Promise<{ financingPrograms: FinancingProgramConfig[] }> => {
        return await apiClient.put<{ financingPrograms: FinancingProgramConfig[] }>(
            '/settings/financing-programs',
            { financingPrograms },
        );
    },

    // Get all scope detail configs
    getScopeDetailConfigs: async (): Promise<ScopeDetailConfigItem[]> => {
        return await apiClient.get<ScopeDetailConfigItem[]>('/settings/scope-detail-config');
    },

    // Get scope detail config for a specific element type
    getScopeDetailConfig: async (elementType: string): Promise<ScopeDetailConfigItem> => {
        return await apiClient.get<ScopeDetailConfigItem>(`/settings/scope-detail-config/${encodeURIComponent(elementType)}`);
    },

    // Update scope detail config for an element type
    updateScopeDetailConfig: async (elementType: string, fields: ScopeDetailField[]): Promise<ScopeDetailConfigItem> => {
        return await apiClient.put<ScopeDetailConfigItem>(`/settings/scope-detail-config/${encodeURIComponent(elementType)}`, { fields });
    },
};

export interface ScopeDetailField {
    label: string;
    key: string;
    required: boolean;
    order: number;
}

export interface ScopeDetailConfigItem {
    id: string;
    organizationId: string;
    elementType: string;
    fields: ScopeDetailField[];
    isActive: boolean;
}

export interface AdministrationCostDefault {
    id: string;
    organizationId: string;
    purpose: string;
    description: string;
    quantity: string;
    unitCost: string;
    displayOrder: number;
    createdAt: string;
    updatedAt: string;
}

export interface FinancingProgramConfig {
    id: string;
    name: string;
    description: string;
    rateFactor: number;
    termMonths: number;
    offered?: boolean;
}

export interface ContractLanguagePack {
    id: string;
    name: string;
    isDefault: boolean;
    /** Two-letter US states that should use this pack (e.g. VA, NC). */
    matchStates: string[];
    contractTermsHtml: string;
    limitedWarrantyHtml: string;
    contractTermsClauseConfig?: Array<{
        id: string;
        title: string;
        requiresInitial: boolean;
    }>;
    /** Option B · Client initials toggles for Section V warranty clauses. */
    limitedWarrantyClauseConfig?: Array<{
        id: string;
        title: string;
        requiresInitial: boolean;
    }>;
}

export interface InvoiceEmailTemplateConfig {
    subject: string;
    body: string;
    placeholders: Array<{ token: string; meaning: string }>;
}

/** Settings → Grok API. The key itself is never returned. */
export interface GrokApiKeyStatus {
    configured: boolean;
    last4: string | null;
    configuredAt: string | null;
}

export interface ProposalTemplateConfig {
    proposalProcessSteps: Array<{ title: string; description: string }>;
    proposalWhyUs: string;
    proposalAboutUs: string;
    proposalNotes: string;
    proposalAccentColor: string;
    /** Contract boilerplate — Section I legal preamble ('' when the company has none). */
    overviewLegalParagraph?: string;
    contractTermsHtml?: string;
    limitedWarrantyHtml?: string;
    languagePacks?: ContractLanguagePack[];
    signatureSectionHtml?: string;
    contractTermsClauseConfig?: Array<{
        id: string;
        title: string;
        requiresInitial: boolean;
    }>;
    limitedWarrantyClauseConfig?: Array<{
        id: string;
        title: string;
        requiresInitial: boolean;
    }>;
    contractSendEmailSubject?: string;
    contractSendEmailHeadline?: string;
    contractSendEmailBody?: string;
    contractSendEmailCta?: string;
    contractReminderEmailSubject?: string;
    contractReminderEmailHeadline?: string;
    contractReminderEmailBody?: string;
    contractReminderEmailCta?: string;
    companyContact?: {
        name?: string;
        phone?: string;
        website?: string;
        address?: string;
    };
}